import React from 'react';

/**
 * Performance Monitoring Service
 *
 * Comprehensive performance tracking for bundle size, loading times,
 * component rendering, and user experience metrics.
 */

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  category: 'loading' | 'rendering' | 'interaction' | 'bundle' | 'network';
  metadata?: Record<string, unknown>;
}

interface BundleMetrics {
  totalSize: number;
  chunkSizes: Record<string, number>;
  loadTimes: Record<string, number>;
  cacheHitRate: number;
}

interface ComponentMetrics {
  componentName: string;
  renderTime: number;
  mountTime: number;
  updateCount: number;
  lastUpdate: number;
}

class PerformanceMonitorService {
  private metrics: PerformanceMetric[] = [];
  private componentMetrics: Map<string, ComponentMetrics> = new Map();
  private bundleMetrics: BundleMetrics = {
    totalSize: 0,
    chunkSizes: {},
    loadTimes: {},
    cacheHitRate: 0,
  };
  private observers: PerformanceObserver[] = [];
  private isEnabled: boolean = true;

  constructor() {
    this.setupPerformanceObservers();
    this.trackInitialLoad();
    this.trackBundleMetrics();
  }

  /**
   * Setup performance observers for automatic metrics collection
   */
  private setupPerformanceObservers(): void {
    if (!('PerformanceObserver' in window)) {
      console.warn('PerformanceObserver not supported');
      return;
    }

    // Navigation timing
    const navObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming;
          this.recordMetric(
            'page_load_time',
            navEntry.loadEventEnd - navEntry.fetchStart,
            'loading',
            {
              domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.fetchStart,
              firstPaint: navEntry.responseStart - navEntry.fetchStart,
              domInteractive: navEntry.domInteractive - navEntry.fetchStart,
            },
          );
        }
      }
    });

    // Resource timing (for chunk loading)
    const resourceObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          const resourceEntry = entry as PerformanceResourceTiming;
          if (resourceEntry.name.includes('.chunk.js') || resourceEntry.name.includes('.js')) {
            const chunkName = this.extractChunkName(resourceEntry.name);
            this.recordMetric(`chunk_load_${chunkName}`, resourceEntry.duration, 'bundle', {
              size: resourceEntry.transferSize,
              cached: resourceEntry.transferSize === 0,
              url: resourceEntry.name,
            });

            // Update bundle metrics
            this.bundleMetrics.loadTimes[chunkName] = resourceEntry.duration;
            if (resourceEntry.transferSize > 0) {
              this.bundleMetrics.chunkSizes[chunkName] = resourceEntry.transferSize;
            }
          }
        }
      }
    });

    // Measure timing (for custom measurements)
    const measureObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'measure') {
          this.recordMetric(entry.name, entry.duration, 'rendering', {
            startTime: entry.startTime,
          });
        }
      }
    });

    // Long task detection
    const longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'longtask') {
          this.recordMetric('long_task', entry.duration, 'rendering', {
            startTime: entry.startTime,
            attribution: (entry as unknown as { attribution?: unknown }).attribution,
          });
        }
      }
    });

    try {
      navObserver.observe({ entryTypes: ['navigation'] });
      resourceObserver.observe({ entryTypes: ['resource'] });
      measureObserver.observe({ entryTypes: ['measure'] });
      longTaskObserver.observe({ entryTypes: ['longtask'] });

      this.observers.push(navObserver, resourceObserver, measureObserver, longTaskObserver);
    } catch (error) {
      console.warn('Error setting up performance observers:', error);
    }
  }

  /**
   * Track initial application load metrics
   */
  private trackInitialLoad(): void {
    // Track when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.recordMetric('dom_ready', performance.now(), 'loading');
      });
    } else {
      this.recordMetric('dom_ready', performance.now(), 'loading');
    }

    // Track when all resources are loaded
    window.addEventListener('load', () => {
      this.recordMetric('window_load', performance.now(), 'loading');
      this.calculateBundleMetrics();
    });

    // Track first paint and first contentful paint
    this.trackPaintMetrics();
  }

  /**
   * Track paint metrics (FP, FCP, LCP)
   */
  private trackPaintMetrics(): void {
    if ('PerformanceObserver' in window) {
      // First Paint and First Contentful Paint
      const paintObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordMetric(entry.name.replace('-', '_'), entry.startTime, 'loading');
        }
      });

      // Largest Contentful Paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        const lcpEntry = lastEntry as unknown as { element?: { tagName?: string }; size?: number };
        this.recordMetric('largest_contentful_paint', lastEntry.startTime, 'loading', {
          element: lcpEntry.element?.tagName,
          size: lcpEntry.size,
        });
      });

      try {
        paintObserver.observe({ entryTypes: ['paint'] });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(paintObserver, lcpObserver);
      } catch (error) {
        console.warn('Error setting up paint observers:', error);
      }
    }
  }

  /**
   * Track bundle-specific metrics
   */
  private trackBundleMetrics(): void {
    // Monitor script loading
    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach((script) => {
      const src = script.getAttribute('src');
      if (src && (src.includes('.js') || src.includes('.chunk'))) {
        const startTime = performance.now();
        script.addEventListener('load', () => {
          const loadTime = performance.now() - startTime;
          const chunkName = this.extractChunkName(src);
          this.recordMetric(`script_load_${chunkName}`, loadTime, 'bundle');
        });
      }
    });
  }

  /**
   * Calculate comprehensive bundle metrics
   */
  private calculateBundleMetrics(): void {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    let totalSize = 0;
    let cachedResources = 0;

    resources.forEach((resource) => {
      if (resource.name.includes('.js') || resource.name.includes('.css')) {
        totalSize += resource.transferSize || 0;
        if (resource.transferSize === 0 && resource.decodedBodySize > 0) {
          cachedResources++;
        }
      }
    });

    this.bundleMetrics.totalSize = totalSize;
    this.bundleMetrics.cacheHitRate = cachedResources / resources.length;

    this.recordMetric('total_bundle_size', totalSize, 'bundle', {
      cacheHitRate: this.bundleMetrics.cacheHitRate,
      resourceCount: resources.length,
    });
  }

  /**
   * Extract chunk name from URL
   */
  private extractChunkName(url: string): string {
    const match = url.match(/([^/]+)\.(?:chunk\.)?js$/);
    return match ? match[1] : 'unknown';
  }

  /**
   * Record a performance metric
   */
  recordMetric(
    name: string,
    value: number,
    category: PerformanceMetric['category'],
    metadata?: Record<string, unknown>,
  ): void {
    if (!this.isEnabled) {
      return;
    }

    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      category,
      metadata,
    };

    this.metrics.push(metric);

    // Log significant metrics in development
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.NODE_ENV === 'development' &&
      (value > 100 || category === 'loading')
    ) {
      console.log(`📊 Performance: ${name} = ${value.toFixed(2)}ms`, metadata);
    }

    // Limit metrics array size
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-500);
    }
  }

  /**
   * Track component rendering performance
   */
  trackComponent(componentName: string): {
    onMount: () => void;
    onUpdate: () => void;
    onUnmount: () => void;
  } {
    const startTime = performance.now();

    return {
      onMount: () => {
        const mountTime = performance.now() - startTime;
        const existing = this.componentMetrics.get(componentName);

        this.componentMetrics.set(componentName, {
          componentName,
          renderTime: mountTime,
          mountTime,
          updateCount: existing?.updateCount || 0,
          lastUpdate: Date.now(),
        });

        this.recordMetric(`component_mount_${componentName}`, mountTime, 'rendering');
      },

      onUpdate: () => {
        const updateTime = performance.now() - startTime;
        const existing = this.componentMetrics.get(componentName);

        if (existing) {
          existing.updateCount++;
          existing.lastUpdate = Date.now();
          existing.renderTime = updateTime;
        }

        this.recordMetric(`component_update_${componentName}`, updateTime, 'rendering');
      },

      onUnmount: () => {
        this.recordMetric(
          `component_unmount_${componentName}`,
          performance.now() - startTime,
          'rendering',
        );
      },
    };
  }

  /**
   * Track user interactions
   */
  trackInteraction(interactionName: string, startTime?: number): void {
    const duration = startTime ? performance.now() - startTime : 0;
    this.recordMetric(`interaction_${interactionName}`, duration, 'interaction');
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    loadingMetrics: PerformanceMetric[];
    renderingMetrics: PerformanceMetric[];
    bundleMetrics: BundleMetrics;
    componentMetrics: ComponentMetrics[];
    recommendations: string[];
  } {
    const loadingMetrics = this.metrics.filter((m) => m.category === 'loading');
    const renderingMetrics = this.metrics.filter((m) => m.category === 'rendering');
    const componentMetrics = Array.from(this.componentMetrics.values());

    const recommendations = this.generateRecommendations();

    return {
      loadingMetrics,
      renderingMetrics,
      bundleMetrics: this.bundleMetrics,
      componentMetrics,
      recommendations,
    };
  }

  /**
   * Generate performance recommendations
   */
  private generateRecommendations(): string[] {
    const recommendations: string[] = [];

    // Check load times
    const loadTime = this.metrics.find((m) => m.name === 'page_load_time')?.value || 0;
    if (loadTime > 3000) {
      recommendations.push(
        'Page load time is slow (>3s). Consider code splitting or optimizing bundle size.',
      );
    }

    // Check bundle size
    if (this.bundleMetrics.totalSize > 1024 * 1024) {
      // 1MB
      recommendations.push('Bundle size is large (>1MB). Consider lazy loading and tree shaking.');
    }

    // Check long tasks
    const longTasks = this.metrics.filter((m) => m.name === 'long_task');
    if (longTasks.length > 5) {
      recommendations.push(
        'Multiple long tasks detected. Consider breaking up heavy computations.',
      );
    }

    // Check component performance
    const slowComponents = Array.from(this.componentMetrics.values()).filter(
      (c) => c.renderTime > 16,
    ); // 60fps threshold
    if (slowComponents.length > 0) {
      recommendations.push(
        `Slow rendering components: ${slowComponents.map((c) => c.componentName).join(', ')}`,
      );
    }

    return recommendations;
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): string {
    return JSON.stringify(
      {
        timestamp: Date.now(),
        metrics: this.metrics,
        bundleMetrics: this.bundleMetrics,
        componentMetrics: Array.from(this.componentMetrics.values()),
        summary: this.getPerformanceSummary(),
      },
      null,
      2,
    );
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics = [];
    this.componentMetrics.clear();
  }

  /**
   * Enable/disable monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Cleanup observers
   */
  destroy(): void {
    this.observers.forEach((observer) => {
      observer.disconnect();
    });
    this.observers = [];
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitorService();

// React hook for component performance tracking
export const usePerformanceTracking = (componentName: string) => {
  const tracker = performanceMonitor.trackComponent(componentName);

  React.useEffect(() => {
    tracker.onMount();
    return tracker.onUnmount;
  }, [tracker]);

  React.useEffect(() => {
    tracker.onUpdate();
  });

  return {
    trackInteraction: (interactionName: string) => {
      const startTime = performance.now();
      return () => performanceMonitor.trackInteraction(interactionName, startTime);
    },
  };
};

export default performanceMonitor;
